import { ProfileCard } from './ProfileCard';
import { ProfileDetailCard } from './ProfileDetailCard';
import type {
    DisplayMember,
    ProfessionalMessageProps,
    ProjectTeamMemberDetailProps,
} from './types';
import { useEffect, useMemo } from 'react';
import { useTracker } from '../../../../../../tracker/src/react-bindings';
/**
 * Displays detailed information about a selected team member with profile card and details
 */
export const ProjectTeamMemberDetail = ({
    currentMember,
    currentTeamMember,
    teamMembers,
    handlePrev,
    handleNext,
    projectID,
    showNavigation = false,
    professionalCountry,
    showEnquiryCTA = true,
    showSpecifierLoginPrompt = false,
    professionalName = '',
}: ProjectTeamMemberDetailProps & { professionalCountry?: string }) => {
    const tracker = useTracker();

    useEffect(() => {
        // this is to track the impression of the team member only once but tracking again when the page is refreshed.
        const storageKey = `projectTeamImpression_${currentTeamMember.ID}`;

        // Return if already triggered
        if (sessionStorage.getItem(storageKey)) return;

        //impression tracking requried 1 sec delay to track the impression
        const timer = setTimeout(() => {
            tracker.log('ProjectTeamImpression', {
                url: new URL(window.location.href),
                data: {
                    Category: currentTeamMember.Category,
                    ProfessionalID: currentTeamMember.ID,
                    Member: currentTeamMember.Name,
                },
            });
            sessionStorage.setItem(storageKey, 'tracked');
        }, 1000);

        // Clear impression sessions on page refresh
        const clearImpression = () => {
            Object.keys(sessionStorage)
                .filter((key) => key.startsWith('projectTeamImpression_'))
                .forEach((key) => sessionStorage.removeItem(key));
        };

        window.addEventListener('beforeunload', clearImpression);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('beforeunload', clearImpression);
        };
    }, [
        currentTeamMember.ID,
        currentTeamMember.Category,
        currentTeamMember.Name,
        tracker,
    ]);

    // Create a ProfessionalMessageProps object for enquiry modal
    const professionalProps: ProfessionalMessageProps = {
        CtaLabel: currentTeamMember.Category || '',
        ProjectID: projectID,
        ID: currentTeamMember.ID,
        Title: currentTeamMember.JobTitle,
        Link: currentTeamMember.Link || '',
        CompanyLogo: currentTeamMember.CompanyLogo || '',
        LibraryLocations: currentTeamMember.LibraryLocations || [],
        LogoBackgroundColor: currentTeamMember.LogoBackgroundColor || '',
        branchOptions: currentTeamMember.BranchOptions || [],
        ArchiproEditorialAccount:
            currentTeamMember.ArchiproEditorialAccount || '',
        CustomEnquiryMessage: currentTeamMember.CustomEnquiryMessage || '',
        AvailableForWork: currentTeamMember.AvailableForWork || false,
        ProfessionalID: currentTeamMember.ProfessionalID,
    };

    // Create a MemberForDisplay for the current selected team member
    const displayMember = useMemo((): DisplayMember => {
        return {
            name: currentTeamMember.Name,
            jobTitle: currentTeamMember.JobTitle,
            detailsPageImageSrc: currentTeamMember.Thumbnail,
            category: currentMember.Category,
            professionalId: currentTeamMember.ProfessionalID,
            professionalName: currentTeamMember.ProfessionalName,
        };
    }, [currentTeamMember, currentMember]);

    // Transform all team members for the ProfileCard component
    const displayFilteredMembers = useMemo((): DisplayMember[] => {
        return teamMembers.map((member) => ({
            name: member.Name,
            jobTitle: member.JobTitle,
            detailsPageImageSrc: member.Thumbnail,
            category: currentMember.Category,
            professionalId: member.ProfessionalID,
            professionalName: member.ProfessionalName,
        }));
    }, [teamMembers, currentMember]);

    if (!displayMember || !currentTeamMember) {
        return null;
    }

    return (
        <>
            <div
                key={currentTeamMember.ID}
                className="flex h-full max-h-[42.5rem] w-full flex-row gap-[8.75rem]"
            >
                {/* Profile Card Section */}
                <ProfileCard
                    member={displayMember}
                    filteredMembers={displayFilteredMembers}
                    handlePrev={handlePrev}
                    handleNext={handleNext}
                    showNavigation={showNavigation}
                />
                <div className="mt-23.5">
                    {/* Profile Detail Card Section */}
                    <ProfileDetailCard
                        key={currentTeamMember.ID}
                        name={currentTeamMember.Name}
                        summary={currentTeamMember.Summary}
                        licenseNumber={currentTeamMember.LicenseNumber}
                        id={currentTeamMember.ID}
                        professionalId={currentTeamMember.ProfessionalID}
                        professionalProps={professionalProps}
                        category={currentMember.Category}
                        professionalCountry={professionalCountry}
                        showEnquiryCTA={showEnquiryCTA}
                        showSpecifierLoginPrompt={showSpecifierLoginPrompt}
                        professionalName={professionalName}
                    />
                </div>
            </div>
        </>
    );
};
